import dayjs, { Dayjs } from "dayjs";
import {
  ReprintRequestRow,
  ReprintRequestStatus,
  ReprintRequestTransactionType,
  RequestedCopy,
} from "../../types/reprintRequest.type";
// Reused verbatim from the sibling Reprint Authorization screen so the two
// governance pages stay visually identical and the Keycloak-group → Thai-label
// map has a single owner.
export { CHARCOAL, AMBER, roleLabel } from "../ReprintAuth/data";

// Status filter. "all" is a UI-only sentinel — the service OMITS the `status`
// query param for it, because the BE enum has no "all" member.
export const STATUS_FILTER: { label: string; value: string }[] = [
  { label: "ทุกสถานะ", value: "all" },
  { label: "รออนุมัติ", value: "PENDING" },
  { label: "อนุมัติแล้ว", value: "APPROVED" },
  { label: "ปฏิเสธแล้ว", value: "REJECTED" },
];

// bill_type_enum — a request may target a sales_order too (wider than the
// reprint-authorization grant's invoice/cashsale pair).
export const TRANSACTION_TYPE_LABEL: Record<
  ReprintRequestTransactionType,
  string
> = {
  invoice: "เครดิต",
  cashsale: "ขายสด",
  sales_order: "ใบสั่งขาย",
};

export const transactionTypeLabel = (
  type: ReprintRequestTransactionType,
): string => TRANSACTION_TYPE_LABEL[type] ?? type;

type StatusChipColor = "warning" | "success" | "error";

export const STATUS_CHIP: Record<
  ReprintRequestStatus,
  { label: string; color: StatusChipColor }
> = {
  PENDING: { label: "รออนุมัติ", color: "warning" },
  APPROVED: { label: "อนุมัติแล้ว", color: "success" },
  REJECTED: { label: "ปฏิเสธแล้ว", color: "error" },
};

// Only a PENDING request can be approved/rejected; the BE 409s on anything else.
export const isReviewable = (row: ReprintRequestRow): boolean =>
  row.status === "PENDING";

// e.g. [{type:"A",quantity:1},{type:"B",quantity:1}] -> "ชุด A + B".
export const getRequestedCopiesLabel = (copies: RequestedCopy[] | null): string =>
  copies && copies.length > 0
    ? `ชุด ${copies.map((copy) => copy.type).join(" + ")}`
    : "-";

// ---- Client-side (page-scoped) filtering ----
// The BE list endpoint exposes ONLY status / requested_by / transaction_uuid —
// there is no free-text or date-range param. These two filters therefore narrow
// the CURRENTLY LOADED page only, and the Filter UI says so explicitly. If they
// need to span the whole result set, the BE must grow `q` / `date_from` /
// `date_to` first (mirroring GET /reprint-authorizations).

const matchesSearch = (row: ReprintRequestRow, search: string): boolean => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;

  return [
    row.transaction_no,
    row.customer_name,
    row.customer_code,
    row.requested_by_name,
    row.route_code,
  ].some((field) => (field ?? "").toLowerCase().includes(needle));
};

const matchesDateRange = (
  row: ReprintRequestRow,
  dateFrom: Dayjs | null,
  dateTo: Dayjs | null,
): boolean => {
  if (!dateFrom && !dateTo) return true;

  const createdAt = dayjs(row.created_at);
  if (!createdAt.isValid()) return false;
  if (dateFrom && createdAt.isBefore(dateFrom.startOf("day"))) return false;
  if (dateTo && createdAt.isAfter(dateTo.endOf("day"))) return false;

  return true;
};

export const filterRowsInPage = (
  rows: ReprintRequestRow[],
  search: string,
  dateFrom: Dayjs | null,
  dateTo: Dayjs | null,
): ReprintRequestRow[] =>
  rows.filter(
    (row) => matchesSearch(row, search) && matchesDateRange(row, dateFrom, dateTo),
  );
